import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
    exportBillingCsvService,
    getBillingCompanyService,
    getBillingPricingService,
    getBillingSummaryService,
    saveBillingPricingService,
    saveBillingServiceService,
    saveBillingUsageService,
    getInvoiceRunService,
    approveInvoicesService,
    getSoniaCompaniesService,
    setSoniaLinkService,
} from "../../../../Services/CRMAdminServices/CRMOperator";

/**
 * Our Fees — Fintrack only.
 *
 * Richard, 3 Oct 2026: our fees are the monthly plan fee — Standard $495, Gold
 * $990, Platinum $2,500 — plus SMS at 10c a message. The expense is what those
 * messages actually cost us. What is left is the margin.
 *
 * One row per client for the month: our fees, our expense, and what is left.
 * Open a client and you get their own lines, their own history, and what they
 * have been worth since they started — which is the question this screen
 * exists to answer.
 *
 * Gameplans are included in the plan fee and are not charged for, so the two Gameplan
 * columns are usage, not money: who signed up that month, and who actually opened it.
 * They come from the SMS engine's own database and are matched to a client by ABN; a
 * client that could not be matched gets a picker rather than a nought that would read
 * as nobody using it.
 *
 * Nothing here is ever visible to a member. The API refuses these routes to
 * anyone but an Operator, so the cost columns cannot leak by a wrong link.
 */

const money = (v: any) =>
    v === null || v === undefined
        ? "—"
        : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 0 }).format(Number(v));

/*  GST is 10% in Australia and is not stored anywhere - every figure on this screen and
    in the database is ex GST, which is how the accountant wants them. */
const GST = 0.10;

/*  The database holds the SMS charge and cost per 100. Richard quotes per message. */
const perSms = (per100: any) =>
    per100 === null || per100 === undefined || per100 === "" ? "" : Number(per100) / 100;
const per100 = (perSmsValue: any) =>
    perSmsValue === "" || perSmsValue === null || perSmsValue === undefined
        ? 0 : Number(perSmsValue) * 100;

const money2 = (v: any) =>
    v === null || v === undefined
        ? "—"
        : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v));

const MONTHS = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

const CRMBilling = () => {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);

    const [summary, setSummary] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const [openId, setOpenId] = useState<number | null>(null);
    const [detail, setDetail] = useState<any>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [pricing, setPricing] = useState<any>(null);
    /*  Richard, 4 Oct 2026: the price list is the point of this screen, not something to
        go looking for. Open by default; the button still folds it away. */
    const [showPricing, setShowPricing] = useState(true);

    /*  The month's invoices. Richard, 3 Oct 2026: built here, approved here, and only
        then pushed to Xero. `picked` is what he has ticked; a client already invoiced
        for this month cannot be ticked at all. */
    const [run, setRun] = useState<any>(null);
    const [showRun, setShowRun] = useState(false);
    const [runLoading, setRunLoading] = useState(false);
    const [picked, setPicked] = useState<number[]>([]);
    const [sending, setSending] = useState(false);

    /*  Every Sonia on the engine, loaded only when a client on this screen could not be
        matched to one by ABN. No unmatched clients, no request. */
    const [sonias, setSonias] = useState<any[] | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r: any = await getBillingSummaryService({ year, month });
            setSummary(r?.data ?? null);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not load the billing summary.");
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => { load(); }, [load]);

    const anyUnmatched = ((summary?.clients ?? []) as any[])
        .some((c: any) => c.isActive && !c.soniaCompanyId);

    useEffect(() => {
        if (!anyUnmatched || sonias !== null) return;
        (async () => {
            try {
                const r: any = await getSoniaCompaniesService();
                setSonias(r?.data ?? []);
            } catch {
                setSonias([]);   // the engine being unreachable is not worth a red toast here
            }
        })();
    }, [anyUnmatched, sonias]);

    const linkSonia = async (companyId: number, soniaCompanyId: number | null) => {
        try {
            await setSoniaLinkService({ companyId, soniaCompanyId });
            toast.success("Saved.");
            load();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "That link could not be saved.");
        }
    };

    const loadPricing = useCallback(async () => {
        try {
            const r: any = await getBillingPricingService();
            setPricing(r?.data ?? null);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not load the price list.");
        }
    }, []);

    useEffect(() => { if (showPricing && !pricing) loadPricing(); }, [showPricing, pricing, loadPricing]);

    const loadRun = useCallback(async () => {
        setRunLoading(true);
        try {
            const r: any = await getInvoiceRunService({ year, month });
            setRun(r?.data ?? null);
            //  Everything not yet sent starts ticked. The common case is "send them all",
            //  and anything already in Xero is left out so it cannot be sent twice.
            setPicked((r?.data?.rows ?? []).filter((x: any) => !x.sentOn).map((x: any) => x.companyId));
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not build the invoice run.");
        } finally {
            setRunLoading(false);
        }
    }, [year, month]);

    useEffect(() => { if (showRun) loadRun(); }, [showRun, loadRun]);

    const approve = async () => {
        if (picked.length === 0) { toast.info("Nothing ticked."); return; }
        setSending(true);
        try {
            const r: any = await approveInvoicesService({ periodYear: year, periodMonth: month, companyIds: picked });
            const d = r?.data ?? {};
            if (d.failed > 0) toast.warning(`${d.sent ?? 0} sent, ${d.failed} could not be sent.`);
            else toast.success(`${d.sent ?? 0} invoice${(d.sent ?? 0) === 1 ? "" : "s"} created in Xero.`);
            await loadRun();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not send the invoices.");
        } finally {
            setSending(false);
        }
    };

    const openCompany = async (companyId: number) => {
        if (openId === companyId) { setOpenId(null); setDetail(null); return; }
        setOpenId(companyId);
        setDetailLoading(true);
        try {
            const r: any = await getBillingCompanyService(companyId, { year, month, months: 12 });
            setDetail(r?.data ?? null);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not load that client.");
        } finally {
            setDetailLoading(false);
        }
    };

    const saveUsage = async (companyId: number, value: string) => {
        const messagesSent = parseInt((value || "0").replace(/\D/g, ""), 10) || 0;
        try {
            const r: any = await saveBillingUsageService({ companyId, periodYear: year, periodMonth: month, messagesSent });
            setSummary(r?.data ?? null);

            // If that client is open underneath, its lines have just changed too.
            if (openId === companyId) {
                const d: any = await getBillingCompanyService(companyId, { year, month, months: 12 });
                setDetail(d?.data ?? null);
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not save that.");
        }
    };

    /*  The price list is Richard's to run. Richard, 4 Oct 2026: "I should be able to
        change / edit these." Until now the Service column came from the database and
        nothing on this screen could touch it - which is how three plans ended up labelled
        one tier out with no way to fix them short of a hand-written UPDATE.

        Retired services are hidden by default. Ten rows were showing and only four were
        still offered, with nothing to say which was which. */
    const [adding, setAdding] = useState(false);
    const [newCode, setNewCode] = useState("");
    const [newName, setNewName] = useState("");

    const saveService = async (body: any) => {
        try {
            await saveBillingServiceService(body);
            await loadPricing();
            await load();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not save that service.");
        }
    };

    const addService = async () => {
        const code = newCode.trim().toUpperCase().replace(/[^A-Z0-9_]/g, "_");
        if (!code || !newName.trim()) {
            toast.error("A new service needs a code and a name.");
            return;
        }
        await saveService({ serviceCode: code, serviceName: newName.trim(), isActive: true });
        setNewCode(""); setNewName(""); setAdding(false);
    };

    const savePrice = async (body: any) => {
        try {
            const r: any = await saveBillingPricingService(body);
            setPricing(r?.data ?? null);
            await load();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not save the price.");
        }
    };

    const exportCsv = async () => {
        try {
            const r: any = await exportBillingCsvService({ year, month });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Fintrack billing ${year}-${String(month).padStart(2, "0")}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e: any) {
            toast.error("Could not build the export.");
        }
    };

    const years = useMemo(() => {
        const out = [];
        for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) out.push(y);
        return out;
    }, [now]);

    const clients = summary?.clients ?? [];
    const marginPct = summary?.totalCharged ? (summary.totalMargin / summary.totalCharged) * 100 : 0;

    return (
        <div className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="f-w-900 mb-0" style={{ color: "blue" }}>Our Fees</h3>
                <div className="d-flex align-items-center gap-2">
                    <select className="form-select form-select-sm" style={{ width: 140 }}
                        value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <select className="form-select form-select-sm" style={{ width: 100 }}
                        value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <Button color="secondary" size="sm" onClick={() => setShowPricing((v) => !v)}>
                        {showPricing ? "Hide prices" : "Prices"}
                    </Button>
                    <Button color="success" size="sm" onClick={() => setShowRun((v) => !v)}>
                        {showRun ? "Hide invoices" : "Invoices"}
                    </Button>
                    <Button color="primary" size="sm" onClick={exportCsv}>Export CSV</Button>
                </div>
            </div>

            {/* The month in four numbers */}
            <div className="d-flex mb-3" style={{ gap: 12, flexWrap: "wrap" }}>
                <Tile label="Our fees (ex GST)" value={money(summary?.totalCharged)} />
                <Tile label="GST" value={money((summary?.totalCharged ?? 0) * GST)} />
                <Tile label="Our fees (inc GST)" value={money((summary?.totalCharged ?? 0) * (1 + GST))} />
                <Tile label="Expense" value={money(summary?.totalCost)} />
                <Tile label="Margin" value={money(summary?.totalMargin)} strong />
                <Tile label="Margin %" value={`${marginPct.toFixed(1)}%`} />
                <Tile label="SMS sent" value={(summary?.totalMessages ?? 0).toLocaleString()} />
            </div>

            {showPricing && pricing && (
                <div className="p-3 mb-3" style={panel}>
                    <h6 className="f-w-700 mb-3">Our fees and what they cost us</h6>
                    <div className="mb-2" style={{ fontSize: 12.5, color: "#767d95" }}>
                        All figures exclude GST. The monthly fee is the client&rsquo;s plan &mdash;
                        Standard $495, Gold $990, Platinum $2,500. Gameplans are included and are
                        not charged for.
                    </div>
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: "24%" }}>Service</th>
                                    <th style={{ width: "13%" }}>Our fee / month</th>
                                    <th style={{ width: "13%" }}>Expense / month</th>
                                    {/*  Richard, 4 Oct 2026: the cap is CLIENTS, not messages. Standard is
                                         "Sonia works 250 of your clients a month"; Gold and Platinum are the
                                         whole database. Messages are 10c on every plan with no limit, so an
                                         SMS allowance was the wrong lever - this is the right one. */}
                                    <th style={{ width: "14%" }} title="How many of their clients Sonia works a month. Blank means the whole database.">Clients included</th>
                                    <th>Margin</th>
                                    <th style={{ width: 140 }}>Offered</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/*  Richard, 4 Oct 2026: he switched two off and they vanished off the
                                     screen, so there was no way to switch them back on. Nothing is
                                     hidden now - a row that is not offered stays where it is and says
                                     so. */}
                                {(pricing.services ?? []).map((s: any) => (
                                    <tr key={s.serviceCode} style={{ opacity: s.isActive ? 1 : 0.55 }}>
                                        <td>
                                            <input type="text" className="form-control form-control-sm f-w-600"
                                                defaultValue={s.serviceName}
                                                title={s.serviceCode}
                                                onBlur={(e) => e.target.value.trim() && e.target.value !== s.serviceName
                                                    && saveService({ serviceCode: s.serviceCode, serviceName: e.target.value.trim() })} />
                                        </td>
                                        <td>
                                            <input type="number" className="form-control form-control-sm"
                                                defaultValue={s.standardMonthlyFee ?? ""}
                                                onBlur={(e) => savePrice({ serviceCode: s.serviceCode, standardMonthlyFee: e.target.value === "" ? null : Number(e.target.value), monthlyCost: s.monthlyCost })} />
                                        </td>
                                        <td>
                                            <input type="number" className="form-control form-control-sm"
                                                defaultValue={s.monthlyCost ?? ""}
                                                onBlur={(e) => savePrice({ serviceCode: s.serviceCode, standardMonthlyFee: s.standardMonthlyFee, monthlyCost: e.target.value === "" ? null : Number(e.target.value) })} />
                                        </td>
                                        <td>
                                            {/*  Blank is unlimited, which is what Gold and Platinum are. A zero
                                                 would read as "none", and none is not what the proposal says. */}
                                            <input type="number" min="0" step="50" className="form-control form-control-sm text-end"
                                                defaultValue={s.includedUnits || ""}
                                                placeholder="whole database"
                                                onBlur={(e) => saveService({ serviceCode: s.serviceCode,
                                                    includedUnits: e.target.value === "" ? 0 : Number(e.target.value),
                                                    unitLabel: "clients" })} />
                                        </td>
                                        <td className="f-w-600">{money2((s.standardMonthlyFee ?? 0) - (s.monthlyCost ?? 0))}</td>
                                        <td>
                                            {/*  Switched off, never deleted. Something was billed against this
                                                 once and a price list that forgets a line cannot explain an old
                                                 invoice. */}
                                            <div className="form-check form-switch mb-0 d-flex align-items-center" style={{ gap: 8 }}>
                                                <input className="form-check-input mt-0" type="checkbox"
                                                    checked={!!s.isActive}
                                                    onChange={(e) => saveService({ serviceCode: s.serviceCode, isActive: e.target.checked })} />
                                                <span style={{ fontSize: 12.5, color: s.isActive ? "#1a7f37" : "#767d95" }}>
                                                    {s.isActive ? "Offered" : "Not offered"}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {/*  Richard thinks and quotes in cents per SMS - "10c per sms". The
                                     database has always held these per 100 and every figure already
                                     billed was worked out from that, so the stored number is left
                                     exactly as it is and only this screen converts. Typing 0.10
                                     here saves 10.00 per hundred, which is the same money. */}
                                <tr style={{ backgroundColor: "#fff8dd" }}>
                                    <td className="f-w-600">SMS, each</td>
                                    <td>
                                        <input type="number" step="0.01" min="0" className="form-control form-control-sm"
                                            defaultValue={perSms(pricing.rates?.messageChargePer100)}
                                            onBlur={(e) => savePrice({ messageChargePer100: per100(e.target.value) })} />
                                    </td>
                                    <td>
                                        <input type="number" step="0.001" min="0" className="form-control form-control-sm"
                                            defaultValue={perSms(pricing.rates?.messageCostPer100)}
                                            onBlur={(e) => savePrice({ messageCostPer100: per100(e.target.value) })} />
                                    </td>
                                    <td className="text-muted" style={{ fontSize: 12.5 }}>
                                        charged on every plan, no limit
                                    </td>
                                    <td className="f-w-600">
                                        {money2(((pricing.rates?.messageChargePer100 ?? 0) - (pricing.rates?.messageCostPer100 ?? 0)) / 100)}
                                    </td>
                                    <td />
                                </tr>

                                {/*  Adding one. The code is what the rest of the system joins on, so it is
                                     typed once and never changed - renaming is what the Service box is for. */}
                                {adding && (
                                    <tr>
                                        <td>
                                            <input type="text" className="form-control form-control-sm mb-1"
                                                placeholder="Name, as it should read" value={newName}
                                                onChange={(e) => setNewName(e.target.value)} />
                                            <input type="text" className="form-control form-control-sm"
                                                placeholder="CODE_LIKE_THIS" value={newCode}
                                                onChange={(e) => setNewCode(e.target.value)} />
                                        </td>
                                        <td colSpan={4} className="text-muted" style={{ fontSize: 12.5 }}>
                                            Add it first, then type the fee, the expense and the client limit on its row.
                                        </td>
                                        <td>
                                            <Button color="success" size="sm" className="mb-1 w-100" onClick={addService}>Add</Button>
                                            <Button color="light" size="sm" className="w-100" onClick={() => setAdding(false)}>Cancel</Button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mt-2" style={{ gap: 12 }}>
                        <small className="text-muted">
                            Saves as soon as you leave a field. A fee set against a client on their own screen wins over the standard price.
                            A service switched off is not offered to new clients; it stays on this list so you can switch it back, and nothing billed against it is lost.
                        </small>
                        <div className="d-flex" style={{ gap: 8 }}>
                            {!adding && (
                                <Button color="primary" size="sm" onClick={() => setAdding(true)}>Add service</Button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showRun && (
                <div className="p-3 mb-3" style={panel}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="f-w-700 mb-0">Invoices for {MONTHS[month - 1]} {year}</h6>
                        {run && (
                            <span style={{ fontSize: 12.5, color: run.xeroConnected ? "#1a7f37" : "#b0392e", fontWeight: 600 }}>
                                {run.xeroConnected ? "Xero connected" : "Xero not connected"}
                            </span>
                        )}
                    </div>

                    <div className="mb-3" style={{ fontSize: 12.5, color: "#767d95" }}>
                        Nothing reaches Xero until you press Approve. Anything already invoiced for this
                        month cannot be ticked &mdash; it has gone once and will not go again. Invoices
                        arrive in Xero as drafts, so they are still not emailed until you approve them there.
                    </div>

                    {runLoading ? (
                        <div className="text-center py-4"><Spinner size="sm" /></div>
                    ) : !run || (run.rows ?? []).length === 0 ? (
                        <p className="mb-0" style={{ fontSize: 14 }}>Nothing to invoice for this month.</p>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 36 }}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={picked.length > 0 && picked.length === (run.rows ?? []).filter((r: any) => !r.sentOn).length}
                                                    onChange={(e) =>
                                                        setPicked(e.target.checked
                                                            ? (run.rows ?? []).filter((r: any) => !r.sentOn).map((r: any) => r.companyId)
                                                            : [])}
                                                />
                                            </th>
                                            <th>Client</th>
                                            <th className="text-end">Monthly fee</th>
                                            <th className="text-end">SMS</th>
                                            <th className="text-end">SMS fee</th>
                                            <th className="text-end">Ex GST</th>
                                            <th className="text-end">GST</th>
                                            <th className="text-end">Total</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(run.rows ?? []).map((r: any) => (
                                            <tr key={r.companyId} style={r.sentOn ? { opacity: 0.6 } : undefined}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        disabled={!!r.sentOn}
                                                        checked={picked.includes(r.companyId)}
                                                        onChange={(e) =>
                                                            setPicked((p) => e.target.checked
                                                                ? [...p, r.companyId]
                                                                : p.filter((id) => id !== r.companyId))}
                                                    />
                                                </td>
                                                <td className="f-w-600">{r.businessName}</td>
                                                <td className="text-end">{money(r.monthlyFee)}</td>
                                                <td className="text-end">{(r.messagesSent ?? 0).toLocaleString()}</td>
                                                <td className="text-end">{money2(r.messagesCharged)}</td>
                                                <td className="text-end">{money2(r.subtotal)}</td>
                                                <td className="text-end text-muted">{money2(r.gst)}</td>
                                                <td className="text-end f-w-700">{money2(r.total)}</td>
                                                <td style={{ fontSize: 12.5 }}>
                                                    {r.lastError
                                                        ? <span style={{ color: "#b0392e" }}>{r.lastError}</span>
                                                        : r.sentOn
                                                            ? <span style={{ color: "#1a7f37" }}>
                                                                Sent{r.xeroInvoiceNumber ? ` · ${r.xeroInvoiceNumber}` : ""}
                                                              </span>
                                                            : <span className="text-muted">Not sent</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ borderTop: "2px solid #ccd2e0" }}>
                                            <td colSpan={5} className="f-w-700">
                                                {run.countToSend} to send · {run.countAlreadySent} already sent
                                            </td>
                                            <td className="text-end f-w-700">{money2(run.totalSubtotal)}</td>
                                            <td className="text-end f-w-700">{money2(run.totalGst)}</td>
                                            <td className="text-end f-w-900">{money2(run.totalDue)}</td>
                                            <td />
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            <div className="d-flex align-items-center gap-3 mt-3">
                                <Button color="success" onClick={approve} disabled={sending || picked.length === 0 || !run.xeroConnected}>
                                    {sending ? "Sending…" : `Approve and send ${picked.length}`}
                                </Button>
                                <span style={{ fontSize: 12.5, color: "#767d95" }}>
                                    {run.xeroConnected
                                        ? `Issued ${new Date(run.issueDate).toLocaleDateString("en-AU")}, due ${new Date(run.dueDate).toLocaleDateString("en-AU")}.`
                                        : "Connect Xero before anything can be sent."}
                                </span>
                            </div>
                        </>
                    )}
                </div>
            )}

            <div className="p-3" style={panel}>
                {loading ? (
                    <div className="text-center py-4"><Spinner size="sm" /></div>
                ) : clients.length === 0 ? (
                    <p className="mb-0" style={{ fontSize: 14 }}>No clients yet.</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Client</th>
                                    <th className="text-end">Monthly fee</th>
                                    <th className="text-end" style={{ width: 110 }}>SMS sent</th>
                                    <th className="text-end" title="People who created their own Gameplan this month">Gameplan signups</th>
                                    <th className="text-end" title="People who opened Gameplan this month, new or returning">Gameplan active</th>
                                    <th className="text-end">Our fees</th>
                                    <th className="text-end">Expense</th>
                                    <th className="text-end">Margin</th>
                                    <th className="text-end">%</th>
                                    <th className="text-end">Worth so far</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map((c: any) => (
                                    <Fragment key={c.companyId}>
                                        <tr>
                                            <td className="f-w-600">
                                                {c.businessName}
                                                {!c.isActive && <Badge color="secondary" className="ms-2">Off</Badge>}
                                            </td>
                                            <td className="text-end">{money(c.servicesCharged)}</td>
                                            <td className="text-end">
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end"
                                                    defaultValue={c.messagesSent}
                                                    onBlur={(e) => saveUsage(c.companyId, e.target.value)}
                                                />
                                            </td>
                                            {/*  Nobody used it and we do not know are different answers, so an
                                                 unmatched client gets a picker, not a nought. */}
                                            {c.soniaCompanyId ? (
                                                <>
                                                    <td className="text-end">{Number(c.gameplanSignups ?? 0).toLocaleString()}</td>
                                                    <td className="text-end">{Number(c.gameplanActive ?? 0).toLocaleString()}</td>
                                                </>
                                            ) : (
                                                <td className="text-end" colSpan={2}>
                                                    {sonias && sonias.length ? (
                                                        <select
                                                            className="form-select form-select-sm"
                                                            defaultValue=""
                                                            onChange={(e) =>
                                                                e.target.value && linkSonia(c.companyId, Number(e.target.value))
                                                            }
                                                        >
                                                            <option value="">Which Sonia?</option>
                                                            {sonias.map((s: any) => (
                                                                <option key={s.companyId} value={s.companyId}>{s.name}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <span className="text-muted" style={{ fontSize: 12 }}>—</span>
                                                    )}
                                                </td>
                                            )}
                                            <td className="text-end">{money(c.totalCharged)}</td>
                                            <td className="text-end text-muted">{money(c.totalCost)}</td>
                                            <td className="text-end f-w-700" style={{ color: c.margin >= 0 ? "#1a7f37" : "#b0392e" }}>
                                                {money(c.margin)}
                                            </td>
                                            <td className="text-end">{Number(c.marginPercent ?? 0).toFixed(0)}%</td>
                                            <td className="text-end f-w-600" title="Our fees since they started">
                                                {money(c.lifetimeCharged)}
                                            </td>
                                            <td className="text-end">
                                                <Button color="light" size="sm" onClick={() => openCompany(c.companyId)}>
                                                    {openId === c.companyId ? "Close" : "Open"}
                                                </Button>
                                            </td>
                                        </tr>

                                        {openId === c.companyId && (
                                            <tr>
                                                <td colSpan={11} style={{ backgroundColor: "#fafbfe" }}>
                                                    {detailLoading ? (
                                                        <div className="text-center py-3"><Spinner size="sm" /></div>
                                                    ) : (
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                <div className="f-w-700 mb-2" style={{ fontSize: 13 }}>
                                                                    {MONTHS[month - 1]} {year}
                                                                </div>
                                                                <table className="table table-sm mb-3">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Line</th>
                                                                            <th className="text-end">Billed</th>
                                                                            <th className="text-end">Cost</th>
                                                                            <th className="text-end">Margin</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(detail?.lines ?? []).map((l: any, i: number) => (
                                                                            <tr key={i}>
                                                                                <td>
                                                                                    {l.name}
                                                                                    {l.lineType === "Messages" && l.quantity > 0 && (
                                                                                        <span className="text-muted"> ({Number(l.quantity).toLocaleString()} sent)</span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="text-end">{money2(l.charged)}</td>
                                                                                <td className="text-end text-muted">{money2(l.cost)}</td>
                                                                                <td className="text-end f-w-600">{money2(l.margin)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>

                                                            <div className="col-md-6">
                                                                <div className="f-w-700 mb-2" style={{ fontSize: 13 }}>What this client has been worth</div>
                                                                <div className="d-flex mb-3" style={{ gap: 12 }}>
                                                                    <Tile label="Our fees to date" value={money(c.lifetimeCharged)} />
                                                                    <Tile label="Margin to date" value={money(c.lifetimeMargin)} strong />
                                                                    <Tile label="Months live" value={c.monthsActive} />
                                                                </div>

                                                                {(detail?.history ?? []).length > 0 && (
                                                                    <table className="table table-sm mb-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Month</th>
                                                                                <th className="text-end">Messages</th>
                                                                                <th className="text-end">Billed</th>
                                                                                <th className="text-end">Margin</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {detail.history.map((h: any) => (
                                                                                <tr key={`${h.periodYear}-${h.periodMonth}`}>
                                                                                    <td>{MONTHS[h.periodMonth - 1]?.slice(0, 3)} {h.periodYear}</td>
                                                                                    <td className="text-end">{Number(h.messagesSent).toLocaleString()}</td>
                                                                                    <td className="text-end">{money(h.totalCharged)}</td>
                                                                                    <td className="text-end f-w-600">{money(h.margin)}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <small className="text-muted d-block mt-2">
                    Type the messages sent for the month straight into the column. Cost and margin are Fintrack only — no member can see this screen.
                </small>
            </div>
        </div>
    );
};

const panel = {
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
} as const;

const Tile = ({ label, value, strong }: { label: string; value: any; strong?: boolean }) => (
    <div style={{
        border: "1px solid #e6e8f0", borderRadius: 8, padding: "10px 16px",
        background: strong ? "#f0f9f2" : "#fff", minWidth: 120,
    }}>
        <div style={{ fontSize: 11.5, color: "#767d95", fontWeight: 600, letterSpacing: 0.3 }}>{label}</div>
        <div style={{ fontSize: 19, fontWeight: 800, color: strong ? "#1a7f37" : "#111528" }}>{value}</div>
    </div>
);

export default CRMBilling;
